import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { getNotificationsStore, type NotificationsSnapshot, type NotificationsStore } from "./notificationsStore";
import type { AppNotification, ConnectionStatus, ServerFrame } from "./types";

export type { AppNotification } from "./types";

const OFF: NotificationsSnapshot = {
  items: [],
  unread: 0,
  loadState: "idle",
  connection: "offline",
  hasMore: false,
  loadingMore: false,
};
const noopSubscribe = () => () => {};
const offSnapshot = () => OFF;

/** Holds the tab's store open while `enabled` (reference counted). */
function useHold(store: NotificationsStore, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    return store.start();
  }, [store, enabled]);
}

export interface UseNotificationsResult extends NotificationsSnapshot {
  markRead: (id: string) => void;
  markAllRead: () => void;
  loadMore: () => void;
  retry: () => void;
}

/**
 * The notification feed for the bell: REST history merged with the live
 * socket (spec §6). Mounting it is what opens the socket.
 */
export function useNotifications(store: NotificationsStore = getNotificationsStore()): UseNotificationsResult {
  useHold(store, true);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const markRead = useCallback((id: string) => store.markRead(id), [store]);
  const markAllRead = useCallback(() => store.markAllRead(), [store]);
  const loadMore = useCallback(() => void store.loadMore(), [store]);
  const retry = useCallback(() => void store.refresh(), [store]);
  return { ...snapshot, markRead, markAllRead, loadMore, retry };
}

/**
 * The socket's state, holding it open while `enabled`. "connected" means the
 * server has finished its replay — the point from which nothing is missed.
 */
export function useNotificationsConnection(
  enabled: boolean,
  store: NotificationsStore = getNotificationsStore(),
): ConnectionStatus {
  useHold(store, enabled);
  const snapshot = useSyncExternalStore(
    enabled ? store.subscribe : noopSubscribe,
    enabled ? store.getSnapshot : offSnapshot,
    enabled ? store.getSnapshot : offSnapshot,
  );
  return snapshot.connection;
}

/** Calls `handler` for every server frame while `enabled` (latest handler wins, no resubscribe). */
export function useNotificationFrames(
  handler: (frame: ServerFrame) => void,
  enabled: boolean,
  store: NotificationsStore = getNotificationsStore(),
): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    return store.onFrame((f) => ref.current(f));
  }, [store, enabled]);
}

/** Calls `handler` for each notification that arrives live (not history, not replay). */
export function useLiveNotifications(
  handler: (n: AppNotification) => void,
  enabled: boolean,
  store: NotificationsStore = getNotificationsStore(),
): void {
  useHold(store, enabled);
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    return store.onLive((n) => ref.current(n));
  }, [store, enabled]);
}
